import React from 'react';
import { Link } from 'react-router-dom';
import './PrivacyPolicy.css'; // Reusing the same CSS for consistent legal document styling

interface Props {
  isEmbedded?: boolean;
}

export default function TermsOfService({ isEmbedded = false }: Props) {
  const year = new Date().getFullYear();
  return (
    <div className={isEmbedded ? "pp-embedded" : "pp-page"}>
      <div className="pp-container">
        <div className="pp-header">
          <h1>Terms of Service</h1>
          <p className="pp-subtitle">Điều khoản Dịch vụ / Terms of Service</p>
          <p className="pp-meta">Effective Date: April 2025 &nbsp;|&nbsp; Last Updated: April 2025</p>
          <p className="pp-meta">Operated by: <strong>Noland Hoang</strong> | Application: <strong>Phanh</strong></p>
        </div>

        <div className="pp-section">
          <h2>1. Acceptance of Terms / Chấp nhận Điều khoản</h2>
          <p>
            By accessing or using the <strong>Phanh</strong> application and platform ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our Service.
          </p>
          <p>
            Bằng việc truy cập hoặc sử dụng ứng dụng và nền tảng <strong>Phanh</strong> ("Dịch vụ"), bạn đồng ý bị ràng buộc bởi các Điều khoản Dịch vụ này. Nếu bạn không đồng ý, vui lòng không sử dụng Dịch vụ.
          </p>
        </div>

        <div className="pp-section">
          <h2>2. Description of Service / Mô tả Dịch vụ</h2>
          <p>
            Phanh provides a booking management and scheduling platform connecting businesses (salons, spas, clinics, etc.) with their customers. We act as an intermediary technology platform.
          </p>
          <p>
            We are not responsible for the actual services provided by the businesses listed on our platform. Any disputes regarding services rendered, pricing, or quality must be resolved directly with the respective business.
          </p>
        </div>

        <div className="pp-section">
          <h2>3. User Accounts and Security / Tài khoản và Bảo mật</h2>
          <ul>
            <li>You must be at least 18 years old to create a business account.</li>
            <li>You are responsible for maintaining the confidentiality of your account credentials (password).</li>
            <li>You agree to notify us immediately of any unauthorized use of your account.</li>
            <li>We reserve the right to suspend or terminate accounts that violate these Terms or engage in fraudulent activities.</li>
          </ul>
        </div>

        <div className="pp-section">
          <h2>4. User Conduct and Prohibited Activities / Hành vi Cấm</h2>
          <p>When using the Service, you agree NOT to:</p>
          <ul>
            <li>Use the platform for any illegal, harmful, or fraudulent purpose.</li>
            <li>Interfere with or disrupt the integrity or performance of the Service (e.g., hacking, spamming).</li>
            <li>Create fake bookings, fake accounts, or submit false reviews.</li>
            <li>Attempt to bypass any security measures or access data belonging to other users.</li>
          </ul>
        </div>

        <div className="pp-section">
          <h2>5. Cancellation and Refund Policy / Chính sách Hủy và Hoàn tiền</h2>
          <p className="pp-highlight">
            <strong>Important:</strong> Phanh does not process payments for the actual services booked through the platform. Cancellation, no-show policies, and refunds are determined and enforced <strong>solely by the individual businesses</strong> you book with.
          </p>
          <p>
            Phanh không xử lý thanh toán cho các dịch vụ được đặt qua nền tảng. Chính sách hủy lịch, vắng mặt và hoàn tiền được quyết định và thực thi <strong>hoàn toàn bởi từng cửa hàng/doanh nghiệp</strong> mà bạn đặt lịch.
          </p>
          <p>
            For Business Owners (Subscription to Phanh Platform): Any subscription fees paid to use the Phanh platform are non-refundable unless otherwise stated in a separate agreement or required by law.
          </p>
        </div>

        <div className="pp-section">
          <h2>6. Intellectual Property / Sở hữu Trí tuệ</h2>
          <p>
            The Service, including its original content, features, functionality, source code, and design, are owned by Noland Hoang and are protected by international copyright, trademark, and intellectual property laws. You may not copy, modify, or distribute any part of the Service without explicit permission.
          </p>
        </div>

        <div className="pp-section">
          <h2>7. Limitation of Liability / Giới hạn Trách nhiệm</h2>
          <p>
            To the maximum extent permitted by applicable law, Phanh and its operator (Noland Hoang) shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses, resulting from:
          </p>
          <ul>
            <li>Your access to or use of or inability to access or use the Service;</li>
            <li>Any conduct or content of any third party (including businesses) on the Service;</li>
            <li>Any services or products provided by businesses found through our platform.</li>
          </ul>
        </div>

        <div className="pp-section">
          <h2>8. Governing Law / Luật Áp dụng</h2>
          <p>
            These Terms shall be governed and construed in accordance with the laws of the Province of Ontario and the federal laws of Canada applicable therein, without regard to its conflict of law provisions.
          </p>
        </div>

        <div className="pp-section">
          <h2>9. Changes to Terms / Thay đổi Điều khoản</h2>
          <p>
            We reserve the right, at our sole discretion, to modify or replace these Terms at any time. We will provide at least 30 days' notice prior to any material new terms taking effect. By continuing to access or use our Service after those revisions become effective, you agree to be bound by the revised terms.
          </p>
        </div>

        <div className="pp-section">
          <h2>10. Contact Information / Liên hệ</h2>
          <p>If you have any questions about these Terms, please contact us:</p>
          <div className="pp-contact">
            <p><strong>Noland Hoang</strong> (Hoàng Phi Anh)</p>
            <p>Email: hoang.phi.anh.2000@gmail.com</p>
          </div>
        </div>

        {!isEmbedded && (
          <div className="pp-footer">
            <p>© {year} Phanh. Copyright by <strong>Noland Hoang</strong> (Hoàng Phi Anh). All rights reserved.</p>
            <Link to="/register" className="pp-back-link">← Back to Register</Link>
          </div>
        )}
      </div>
    </div>
  );
}
